import PageLoader from '@/common/service/flow/page-loader';
import { flowTypes, pageConfig } from '@/common/service/page-config';
import { loadSelectByPersist, plansDataMapper ,filterCapacity } from '@/common/service/plan-data-mapper';
import { buySubscription, freeUpgrade, getPlanMap ,capacityConfig } from '@/apis';
import { pollingEventStatus } from '@/common/polling';
import store from '@/store/index';
import { cloneDeep, includes, filter, find, get } from 'lodash';
import { gotoSuccessPage, gotoTrialSuccessPage, gotoWiseSuccessPage, upgradeSuccessPage } from '@/common/service/common';
import { loadDefaultPlansByCartId, loadDefaultPlansByPreloadCoupon } from '@/views/config/coupon/helper/preload';
import { isEmptyVal } from '@/utils';
import { planQueryParser, addToCartByCategoryAndBillingCycle, addToCartByType, getAudioCountriesParams } from '@/common/service/flow/utils';
import { localNumberStore, tollFreeNumberStore } from '@/plans/ZOOM_PHONE/store';
import { computedPbxCallingPlans, computedPbxPlans, PBX_SUB_TYPES } from '@/common/service/computers/plans/pbx';
import { preCheckForPbx } from '@/common/service/validate/pbx';
import { regionTypesEnum } from "@/views/checkout/constant";
import { preCheckPartnerRefer } from '@/views/config/partner-refer/helper/validator';
import { getPartnerRefer } from '@/views/config/partner-refer/helper/utils';
import { loadDefaultPlansByPartnerReferral } from '@/views/config/partner-refer/helper/preload';
import { VIEWS } from '@/views/bridge';
import { getPlanTypeByCategory } from '@/common/service/parse';
import { AccountType, BASE_PLAN_SUB_TYPES, BILLING_CYCLE, ZOOM_MEETING_PRO_MIN_LICENSE, planTypes, PBX_CATEGORYS } from '@/common/constants';
import { beforeCheckForCoupon } from '@/views/config/coupon/helper/validator';
import Flow from '@/flows/flow';
import { addBasePlanByTargetAddonPlan ,addBasePlanToStandAlone} from '@/common/product';
import { removeEmptyValue } from '@/common/service/checkout-utils';
import { checkCountriesRegionsOption } from '@/common/service/validate/tollfree';
import { APM, WISE } from '@/views/checkout/payment/helper/constant';
import { isBizPlusContext } from '@/plans/ZOOM_MEETING/common/utils';
import { checkBizPlusLicensePromise } from '@/plans/ZOOM_ONE_BUNDLE/utils';

const TRIAL_SUCCESS_CODE = 'freetrial_success';

const checkSelectedPlan = (planMapFlat = []) => {
  return planMapFlat.find(item => item.cartInfo.selected_number > 0);
}

function loadDefaultTargetPlanByQuery(planMapFlat) {
  // add target plan to cart
  let { from, period = BILLING_CYCLE.ANNUAL, quantity = 1 } = pageConfig;
  quantity = Number.parseInt(quantity) || 1
  // addon plan categories
  const { category_codes = [], fromType } = planQueryParser(from);
  // add all search addon plans to cart
  category_codes.forEach(code => {
    let quantity_ = code === category_codes[0] ? quantity : 1;
    if(isBizPlusContext) quantity_ = ZOOM_MEETING_PRO_MIN_LICENSE;
    addToCartByCategoryAndBillingCycle(planMapFlat, fromType, code, period, quantity_);
  });
  // HARDCODE
  if(isBizPlusContext){
    addToCartByCategoryAndBillingCycle(planMapFlat, fromType, PBX_CATEGORYS.ZVP, period, 1);
  }
}

const loadDefaultDepedentPlanByQuery = (planMapFlat) => {
  const { from, period = BILLING_CYCLE.ANNUAL , plan = BASE_PLAN_SUB_TYPES.PRO } = pageConfig;
  const selectedPlans = filter(planMapFlat, (p) => p?.cartInfo?.selected_number > 0);
  const addPlanType = getPlanTypeByCategory(from);

  const targetPlan = find(selectedPlans, (p) => p.type === addPlanType) || {};
  const dependPlanTypeList = get(targetPlan, 'dependBasePlanTypeList', []);
  const dependTargetPlan = find(selectedPlans, (p) => includes(dependPlanTypeList, p.type));

  addBasePlanByTargetAddonPlan(planMapFlat, targetPlan, dependTargetPlan?.type, period, getPlanTypeByCategory(plan), isBizPlusContext && ZOOM_MEETING_PRO_MIN_LICENSE);
  addBasePlanToStandAlone(planMapFlat, targetPlan, plan, period);

  return planMapFlat;
}

function loadDefaultSelectByQuery(planMapFlat) {
  const hasSelectedPlan = checkSelectedPlan(planMapFlat);

  if (!hasSelectedPlan) {
    loadDefaultTargetPlanByQuery(planMapFlat);
    loadDefaultDepedentPlanByQuery(planMapFlat);
  }

  return planMapFlat;
}

function loadDefaultByQueryWhenCartIsEmpty(planMapFlat) {
  let { plan = BASE_PLAN_SUB_TYPES.PRO, period = BILLING_CYCLE.ANNUAL } = pageConfig;
  const hasSelectedPlan = checkSelectedPlan(planMapFlat);

  if (hasSelectedPlan) return planMapFlat;

  let planType = getPlanTypeByCategory(plan);

  // In order to solve some undefined category types,
  // such as z1s, which cause planType to not be found, a pro can be loaded by default.
  if (isEmptyVal(planType)) {
    plan = BASE_PLAN_SUB_TYPES.PRO;
    planType = getPlanTypeByCategory(plan);
  }

  addToCartByCategoryAndBillingCycle(planMapFlat, planType, plan, period, 1);
  planType === planTypes.VIDEO_SDK && addToCartByType(planMapFlat, planType, 1)

  return planMapFlat;
}

const filterDisabledPlans = (allPlanResult = {}) => {
  const { disablePlanCodeList = [], disablePlanCategoryList = [] } = allPlanResult;

  return (planMapFlat = []) => {
    return filter(planMapFlat, (p) => {
      return (includes(disablePlanCodeList, p?.plan_code) === false
        && includes(disablePlanCategoryList, p?.parent_category) === false)
    });
  };
};

// isFreeTrialAccount
function loadFreeTrialAccount(planMapFlat) {
  const hasSelectedPlan = planMapFlat.find(item => item.cartInfo.selected_number > 0);
  if (!hasSelectedPlan) {
    // Add Biz
    const { period = 'annual' } = pageConfig;
    const bizPlans = planMapFlat.filter(item => item.category === 'biz');
    const bizPlan = bizPlans.find(item => item.billing_cycle === period) || bizPlans[0];
    bizPlan.cartInfo.selected_number = 10;
    // Add Webinar
    const webinar500Plans = planMapFlat.filter(item => item.category === 'webinar500');
    const webinar500Plan = webinar500Plans.find(item => item.billing_cycle === period) || webinar500Plans[0];
    webinar500Plan.cartInfo.selected_number = 10;
    // Add Large meeting
    const large500Plans = planMapFlat.filter(item => item.category === 'large500');
    const large500Plan = large500Plans.find(item => item.billing_cycle === period) || large500Plans[0];
    large500Plan.cartInfo.selected_number = 10;
    return planMapFlat;
  }
  return planMapFlat;
}

const initShoppingCartForByNow = async state => {
  const { persist } = state;
  // load data
  const { result = {} } = await getPlanMap();
  const { result : capacityList } = await capacityConfig({flowType: flowTypes.buyNow});
  const buyNowLoader = new PageLoader(result);
  buyNowLoader.decorate('plansDataMapper', plansDataMapper);
  buyNowLoader.decorate('filterDisabledPlans', filterDisabledPlans(result));
  buyNowLoader.decorate('filterCapacity', filterCapacity(capacityList));
  buyNowLoader.decorate('loadByPersist', loadSelectByPersist(persist));
  buyNowLoader.decorate('loadDefaultPlansByCartId', loadDefaultPlansByCartId);
  buyNowLoader.decorate('loadDefaultSelectByCoupon', loadDefaultPlansByPreloadCoupon());
  buyNowLoader.decorate('loadDefaultPlansByPartnerReferral', loadDefaultPlansByPartnerReferral);
  buyNowLoader.decorate('loadDefaultSelectByQuery', loadDefaultSelectByQuery);
  buyNowLoader.decorate('loadDefaultByQueryWhenCartIsEmpty', loadDefaultByQueryWhenCartIsEmpty);
  const data = await buyNowLoader.dispatch();

  return {
    allPlanMap: result,
    planMapFlat: data,
  };
};

const initShoppingCartForBasicToPro = async state => {
  const { persist } = state;
  // load data
  const { result = {} } = await getPlanMap();
  const buyNowLoader = new PageLoader(result);
  buyNowLoader.decorate('plansDataMapper', plansDataMapper);
  buyNowLoader.decorate('filterDisabledPlans', filterDisabledPlans(result));
  buyNowLoader.decorate('loadByPersist', loadSelectByPersist(persist));
  buyNowLoader.decorate('loadDefaultPlansByCartId', loadDefaultPlansByCartId);
  buyNowLoader.decorate('loadDefaultSelectByCoupon', loadDefaultPlansByPreloadCoupon());
  buyNowLoader.decorate('loadDefaultPlansByPartnerReferral', loadDefaultPlansByPartnerReferral);
  if (pageConfig.isFreeTrialAccount) {
    buyNowLoader.decorate('loadFreeTrialAccount', loadFreeTrialAccount);
  } else {
    buyNowLoader.decorate('loadDefaultSelectByQuery', loadDefaultSelectByQuery);
  }
  buyNowLoader.decorate('loadDefaultByQueryWhenCartIsEmpty', loadDefaultByQueryWhenCartIsEmpty);
  const data = await buyNowLoader.dispatch();

  return {
    allPlanMap: result,
    planMapFlat: data,
  };
}

export class BaseCheckoutForSubmit {
  getPreAssignPhoneNumber() {
    const _preAssignPhoneNumber = {};
    const getters = store.getters;
    const localNumberState = localNumberStore.state;
    const tollFreeNumberState = tollFreeNumberStore.state;
    const selectedCallingPlan = computedPbxCallingPlans(getters.getSelectedPlans)[0];
    const selectedTFPlan = computedPbxPlans(getters.getSelectedPlans).find(item => item.sub_type === PBX_SUB_TYPES.TOLLFREE);

    if (selectedCallingPlan && localNumberState.showNumberList) {
      _preAssignPhoneNumber.tollNumbers = localNumberState.pickedNumberList.map(item => ({
        ...item,
        planCode: selectedCallingPlan.plan_code,
      }))
    }
    if (selectedTFPlan && tollFreeNumberState.showNumberList) {
      _preAssignPhoneNumber.tollFreeNumbers = tollFreeNumberState.pickedNumberList.map(item => ({
        ...item,
        planCode: selectedTFPlan.plan_code,
      }));
    }
    return _preAssignPhoneNumber;
  }

  generateCheckoutParams() {
    const getters = store.getters;

    const addressInfo = getters.addressInfo;
    const { apmInfo } = store.getters;
    const { couponCode = '' } = getters.couponState;
    let { paymentResponse, paymentGateway } = getters.paymentMethodInfo;
    const partnerReferParams = getters.eligiblePartnerReferOffer ? { referralCode: getPartnerRefer().referralCode } : {};
    if (apmInfo.isApm) {
      paymentResponse = JSON.stringify({
        integrationType: APM
      })
    }
    const audioCountriesParams = getAudioCountriesParams(store);
    const jointsaleCode = getters.partnerRefer.jointsaleCode;
    const params = {
      taxInfo: getters.taxInfo,

      contact: cloneDeep([removeEmptyValue(getters.billToContact), removeEmptyValue(getters.soldToContact)]),
      paymentInfo: { paymentResponse, paymentGateway },
      orderData: {
        planList: getters.selectedPlanListPayLoad,
        coupon: { fromCampaignLink: getters.fromCampaignLink, code: couponCode },
      },
      extension: {
        billingChannel: 'billing_page',
        scheduledEmailId: '',
        usageType: addressInfo?.usageType,
        useCase: addressInfo?.useCase,
        otherUseCase: addressInfo?.otherUseCase,
        ...partnerReferParams,
        ...(pageConfig.mid ? { meetingId: pageConfig.mid} : {}),
        ...(!audioCountriesParams ? {} : { tollFreeProfileData: audioCountriesParams }),
        ...(jointsaleCode ? { jointsaleCode } : {}),
      },
      agreements:{},
    };
    // for agreements
    if (Reflect.has(getters, 'agreements')) {
      if(getters.agreements.regionTypes !== regionTypesEnum.NOLIMITED_REGIONS) {
        params.agreements.isMarketingReview = !!getters.agreements.marketingReview;
      }
    }
    const preAssignPhoneNumber = this.getPreAssignPhoneNumber();
    if (!isEmptyVal(preAssignPhoneNumber)) {
      params.extension.preAssignPhoneNumber = preAssignPhoneNumber;
    }

    if (addressInfo?.isForEdu) {
      params.extension.coppa = addressInfo.coppa.join(',');

      if (addressInfo?.usageType !== 'business' && !addressInfo?.companyName) {
        const { orgName = '', orgCount = '' } = addressInfo;
        params.contact.forEach(c => {
          c.companyName = orgName;
          c.employeeCount = orgCount;
        });
      }
    }

    return params;
  }
}

class CheckOutForBuyNowSubmit extends BaseCheckoutForSubmit {
  submit = async () => {
    const { apmInfo, agreements: { regionTypes = regionTypesEnum.NOLIMITED_REGIONS } } = store.getters;
    if (apmInfo.isApm) {
      const eventResult = await pollingEventStatus({ rps: true });
      const successParams = {
        integrationType: APM,
        apmMethodName: apmInfo.apmPaymentMethodName
      }
      apmInfo.apmPaymentMethodName === WISE ? gotoWiseSuccessPage({ wiseStateValue: apmInfo.wiseStateValue }) : gotoSuccessPage(successParams);
      return {
        eventResult
      };
    }
    const loggedIn = window.$context?.is?.loggedIn;
    const params = this.generateCheckoutParams();
    // just avoid place order api is fail and user have been created,
    // so when the customer refresh page, will recognized free account, should call free upgrade api
    let eventResult;
    eventResult = await (loggedIn ? freeUpgrade(params) : buySubscription(params));
    if(![TRIAL_SUCCESS_CODE].includes(eventResult?.result)){
      eventResult = await pollingEventStatus({ rps: true });
    };
    const successParams = loggedIn ? undefined : { coppa: params?.extension?.coppa };

    // GDPR params
    let GDPRParams = {};
    if (Reflect.has(params, 'agreements') && Reflect.has(params, 'contact')) {
      // Euro countries
      if(regionTypes === regionTypesEnum.COUNTRY_EUROPEAN_ECONOMIC_AREA) {
        const enableMarketingReviewEnumMap = {
          DISABLE: 0,
          ENABLE: 1,
        }
        GDPRParams = {
          email: params.contact[0]?.email,
          source: 'USER_SIGNUP',
          opt: params.agreements.isMarketingReview ? enableMarketingReviewEnumMap.ENABLE: enableMarketingReviewEnumMap.DISABLE
        };
      }
    }

    if(eventResult?.result === TRIAL_SUCCESS_CODE){
      gotoTrialSuccessPage({ ...successParams, GDPRParams });
    }else {
      gotoSuccessPage({ ...successParams, GDPRParams });
    }

    return {
      eventResult,
    };
  }
}

class CheckOutForBasic2ProSubmit extends BaseCheckoutForSubmit {
  submit = async () => {
    const { withCcAccount, apmInfo } = store.getters;

    if (apmInfo.isApm) {
      const eventResult = await pollingEventStatus({ rps: true });

      const successParams = {
        integrationType: APM,
        apmMethodName: apmInfo.apmPaymentMethodName
      }
      const trackingName = withCcAccount ? 'Free w/ CC Upgrade' : 'Free w/o CC Upgrade'
      apmInfo.apmPaymentMethodName === WISE ? gotoWiseSuccessPage({ wiseStateValue: apmInfo.wiseStateValue, flowType: trackingName}) : gotoSuccessPage(successParams);

      return {
        eventResult,
        trackingName
      };
    }

    const eventResult = await (withCcAccount
      ? this.submitForFreeWithCc()
      : this.submitForFreeWithOutCc());

    if(eventResult?.result === TRIAL_SUCCESS_CODE){
      gotoTrialSuccessPage({});
    } else {
        // TODO: Need enhance! The backend will check the account type for the basic2pro type (this needs to be removed for successPage)
        upgradeSuccessPage('upgrade');
    }

    return {
      eventResult,
      trackingName: withCcAccount ? 'Free w/ CC Upgrade' : 'Free w/o CC Upgrade',
    };
  }
  submitForFreeWithOutCc = async () => {
    const params = this.generateCheckoutParams();
    const eventResult = await freeUpgrade(params);
    if(eventResult?.result === TRIAL_SUCCESS_CODE) return Promise.resolve(eventResult);
    return pollingEventStatus({ rps: true });
  }
  submitForFreeWithCc = async () => {
    const { orderData, extension } = this.generateCheckoutParams();
    const params = { orderData, extension };
    const eventResult = await freeUpgrade(params);
    if(eventResult?.result === TRIAL_SUCCESS_CODE) return Promise.resolve(eventResult);
    return pollingEventStatus({ rps: true });
  }
}

const preCheckBuyNow = async () => {
  await preCheckForPbx()
  await beforeCheckForCoupon();
  await checkCountriesRegionsOption();
}

const resolveView = () => VIEWS.CONFIG;

const preCheckForSwitchPlan = async (oldPlanList, newPlanList, soldToCountry) => {
  await preCheckPartnerRefer(oldPlanList, newPlanList, soldToCountry);
}

export default {
  [flowTypes.buyNow]: class extends Flow {
    view(){ return resolveView() }
    init() { return initShoppingCartForByNow(...arguments) }
    beforeContinue() { return checkBizPlusLicensePromise() }
    beforeUpdate() { return preCheckForSwitchPlan(...arguments) }
    beforeSubmit() { return preCheckBuyNow() }
    submit() { return (new CheckOutForBuyNowSubmit()).submit() }
  },
  [flowTypes.basic2pro]: class extends Flow {
    view() {
      const compoundFreeWithccPlan = [
        planTypes.ZONE_BASIC_PLUS,
        planTypes.ZONE_PPM,
      ];
      if(
        compoundFreeWithccPlan.includes(getPlanTypeByCategory(pageConfig.plan)) &&
        pageConfig.accountType === AccountType.ACCOUNT_FREE_WITH_CC
      ) {
        return VIEWS.COMPOUND;
      }
      return VIEWS.CONFIG;
    }
    init(){ return initShoppingCartForBasicToPro(...arguments) }
    beforeContinue() { return checkBizPlusLicensePromise() }
    beforeUpdate() { return preCheckForSwitchPlan(...arguments) }
    beforeSubmit() { return preCheckBuyNow() }
    submit() { return (new CheckOutForBasic2ProSubmit()).submit() }
  }
};

